import {
  IInitialViewpoint,
  IViewpointBimFile,
  IViewpointComponent,
} from "Data/interfaces/Activities/IViewpointData";

type ModelDictionary = Map<string, Autodesk.Viewing.Model>;

export class ViewpointRestoreService {
  private static normalizeUrn(raw?: string | null): string | null {
    if (!raw || raw.trim().length === 0) {
      return null;
    }
    return raw.startsWith("urn:") ? raw : `urn:${raw}`;
  }

  private static stripUrnPrefix(raw: string): string {
    return raw.replace(/^urn:/i, "");
  }

  private static collectRequiredModelUrns(viewpoint: IInitialViewpoint): Set<string> {
    const urns = new Set<string>();

    viewpoint.BimFiles?.forEach((file) => {
      const normalized =
        ViewpointRestoreService.normalizeUrn(file.Reference) ??
        ViewpointRestoreService.normalizeUrn(file.Filename);

      if (normalized) {
        urns.add(normalized);
      }
    });

    return urns;
  }

  private static waitForRequiredModels(
    viewer: Autodesk.Viewing.GuiViewer3D,
    viewpoint: IInitialViewpoint,
    onReady: () => void,
  ): void {
    const requiredUrns = ViewpointRestoreService.collectRequiredModelUrns(viewpoint);
    const GEOMETRY_LOADED_EVENT =
      (Autodesk as any)?.Viewing?.GEOMETRY_LOADED_EVENT ?? Autodesk?.Viewing.GEOMETRY_LOADED_EVENT;
    const MODEL_COMPLETED_EVENT =
      (Autodesk as any)?.Viewing?.MODEL_COMPLETED_EVENT ??
      (Autodesk as any)?.Viewing?.OBJECT_TREE_CREATED_EVENT ??
      Autodesk?.Viewing.OBJECT_TREE_CREATED_EVENT;

    const hasAllRequiredModels = () => {
      const allModels =
        (viewer.getAllModels && viewer.getAllModels()) ||
        (viewer.model ? [viewer.model] : []);

      if (!allModels || allModels.length === 0) {
        return false;
      }

      const normalizedLoaded = new Set<string>();
      const strippedLoaded = new Set<string>();
      const allModelsLoaded = allModels.every((model: Autodesk.Viewing.Model) => {
        const loaderDone =
          typeof (model as any).isLoadDone === "function"
            ? (model as any).isLoadDone()
            : true;
        return loaderDone;
      });

      allModels.forEach((model) => {
        const data = model?.getData?.();
        const modelUrn = data?.urn ?? (model as any)?.loader?.svfUrn;
        const normalized = ViewpointRestoreService.normalizeUrn(modelUrn);
        if (normalized) {
          normalizedLoaded.add(normalized);
          strippedLoaded.add(ViewpointRestoreService.stripUrnPrefix(normalized));
        }
      });

      if (requiredUrns.size === 0) {
        return normalizedLoaded.size > 0 && allModelsLoaded;
      }

      return Array.from(requiredUrns).every((urn) => {
        const normalized = ViewpointRestoreService.normalizeUrn(urn);
        if (!normalized) {
          return true;
        }
        const stripped = ViewpointRestoreService.stripUrnPrefix(normalized);
        return normalizedLoaded.has(normalized) || strippedLoaded.has(stripped);
      });
    };

    if (hasAllRequiredModels()) {
      onReady();
      return;
    }

    let resolved = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const cleanup = () => {
      if (GEOMETRY_LOADED_EVENT !== undefined) {
        viewer.removeEventListener(GEOMETRY_LOADED_EVENT, handleModelEvent);
      }
      if (MODEL_COMPLETED_EVENT !== undefined) {
        viewer.removeEventListener(MODEL_COMPLETED_EVENT, handleModelEvent);
      }
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
        timeoutId = undefined;
      }
    };

    const handleModelEvent = () => {
      if (resolved || !hasAllRequiredModels()) {
        return;
      }

      resolved = true;
      cleanup();
      onReady();
    };

    if (GEOMETRY_LOADED_EVENT !== undefined) {
      viewer.addEventListener(GEOMETRY_LOADED_EVENT, handleModelEvent);
    }
    if (MODEL_COMPLETED_EVENT !== undefined) {
      viewer.addEventListener(MODEL_COMPLETED_EVENT, handleModelEvent);
    }

    timeoutId = setTimeout(() => {
      if (resolved) {
        return;
      }
      resolved = true;
      cleanup();
      onReady();
    }, 10000);
  }

  static applyViewpoint(viewer: Autodesk.Viewing.GuiViewer3D, viewpoint: IInitialViewpoint): void {
    if (!viewer || !viewpoint) {
      console.warn("Viewer or viewpoint is null/undefined");
      return;
    }

    const runRestore = () => {
      const THREE = (window as any).THREE || (Autodesk as any).THREE;
      if (!THREE) {
        console.warn("THREE namespace not available. Skipping viewpoint restore.");
        return;
      }

      const modelMap = this.buildModelDictionary(viewer);
      this.restoreCamera(viewer, viewpoint.Camera);
      this.restoreVisibility(viewer, viewpoint.Visibility, modelMap);
      this.restoreSelection(viewer, viewpoint.Selection, modelMap);
      this.restoreBimFiles(viewer, viewpoint.BimFiles, modelMap);

      viewer.impl.sceneUpdated(true);
      viewer.impl.invalidate(true, true, true);

      // Wait for camera to settle before applying clipping planes
      // Camera adjustments can remove planes if applied too early
      let cameraSettleTimeout: ReturnType<typeof setTimeout> | null = null;
      const CAMERA_SETTLE_DELAY = 200;
      
      const applyClippingPlanesAfterCameraSettles = () => {
        this.restoreClippingPlanes(viewer, viewpoint.ClippingPlanes);
        viewer.removeEventListener(Autodesk?.Viewing.CAMERA_CHANGE_EVENT, handleCameraChange);
      };
      
      const handleCameraChange = () => {
        if (cameraSettleTimeout) {
          clearTimeout(cameraSettleTimeout);
        }
        cameraSettleTimeout = setTimeout(applyClippingPlanesAfterCameraSettles, CAMERA_SETTLE_DELAY);
      };
      
      viewer.addEventListener(Autodesk.Viewing.CAMERA_CHANGE_EVENT, handleCameraChange);
      
      setTimeout(() => {
        if (cameraSettleTimeout) {
          clearTimeout(cameraSettleTimeout);
        }
        viewer.removeEventListener(Autodesk?.Viewing.CAMERA_CHANGE_EVENT, handleCameraChange);
        this.restoreClippingPlanes(viewer, viewpoint.ClippingPlanes);
      }, 1000);
    };

    ViewpointRestoreService.waitForRequiredModels(viewer, viewpoint, runRestore);
  }

  private static buildModelDictionary(viewer: Autodesk.Viewing.GuiViewer3D): ModelDictionary {
    const models = viewer.getAllModels ? viewer.getAllModels() : [viewer.model];
    const dictionary: ModelDictionary = new Map();

    if (!models) {
      return dictionary;
    }

    models.forEach((model) => {
      const urn = model?.getData()?.urn;
      if (!urn) {
        return;
      }

      dictionary.set(urn, model);
      if (!urn.startsWith("urn:")) {
        dictionary.set(`urn:${urn}`, model);
      } else {
        dictionary.set(urn.replace(/^urn:/, ""), model);
      }
    });

    return dictionary;
  }

  /**
   * Restores the camera viewpoint using the viewer's navigation API.
   */
  private static restoreCamera(viewer: Autodesk.Viewing.GuiViewer3D, cameraDto?: IInitialViewpoint["Camera"]): void {
    if (!cameraDto) {
      console.warn("No camera data provided");
      return;
    }

    if (typeof viewer.restoreState !== "function") {
      console.error("viewer.restoreState is not available. Cannot restore viewpoint.");
      return;
    }

    const THREE = (window as any).THREE || (Autodesk as any).THREE;
    if (!THREE) {
      console.warn("THREE not available");
      return;
    }

    // Get the globalOffset that the Viewer automatically applied to the model
    let globalOffset = new THREE.Vector3(0, 0, 0);
    
    const allModels = viewer.getAllModels ? viewer.getAllModels() : [viewer.model].filter(Boolean);
    
    if (allModels && allModels.length > 0) {
      const model = allModels[0];
      const data = model?.getData();
      
      if (data && data.globalOffset) {
        globalOffset.x = data.globalOffset.x || 0;
        globalOffset.y = data.globalOffset.y || 0;
        globalOffset.z = data.globalOffset.z || 0;
      }
    }

    // Adjust camera position by the globalOffset that was applied to center the model
    const position = new THREE.Vector3(
      cameraDto.ViewPoint.X - globalOffset.x,
      cameraDto.ViewPoint.Y - globalOffset.y,
      cameraDto.ViewPoint.Z - globalOffset.z
    );
    
    const viewerDirectionVec = new THREE.Vector3(cameraDto.Direction.X, cameraDto.Direction.Y, cameraDto.Direction.Z).normalize();
    const viewerUpVec = new THREE.Vector3(cameraDto.UpVector.X, cameraDto.UpVector.Y, cameraDto.UpVector.Z).normalize();

    // Calculate a reasonable distance for the target
    let targetDistance = 100; // default fallback
    
    if (allModels && allModels.length > 0) {
      const bbox = allModels[0].getBoundingBox();
      if (bbox) {
        const size = bbox.getSize(new THREE.Vector3());
        targetDistance = (size.length() / 2) * 0.41;
      }
    }
    
    // Calculate target position from camera position + direction * distance
    const targetPosition = position.clone().add(viewerDirectionVec.clone().multiplyScalar(targetDistance));
    
    const isOrthographic = cameraDto.Type === "Orthogonal";
    
    // Set camera directly using navigation API
    const camera = viewer.navigation.getCamera();
    camera.position.set(position.x, position.y, position.z);
    camera.target.set(targetPosition.x, targetPosition.y, targetPosition.z);
    camera.up.set(viewerUpVec.x, viewerUpVec.y, viewerUpVec.z);
    camera.aspect = cameraDto.AspectRatio || camera.aspect;
    
    if (isOrthographic) {
      camera.isPerspective = false;
      camera.orthoScale = cameraDto.ViewToWorldScale || targetDistance;
    } else {
      camera.isPerspective = true;
      camera.fov = cameraDto.FieldOfView || 45;
    }
    
    viewer.navigation.setView(camera.position, camera.target);
    viewer.navigation.setCameraUpVector(camera.up);
    viewer.navigation.setVerticalFov(camera.fov, true);
  }

  private static restoreVisibility(
    viewer: Autodesk.Viewing.GuiViewer3D,
    visibility: IInitialViewpoint["Visibility"],
    modelMap: ModelDictionary
  ): void {
    viewer.showAll();

    if (!visibility) {
      return;
    }

    const exceptions = this.groupComponentsByModel(visibility.Exceptions, modelMap);

    if (!visibility.DefaultVisibility) {
      viewer.hideAll();
      exceptions.forEach((dbIds, model) => {
        if (dbIds.length > 0) {
          viewer.show(dbIds, model);
        }
      });
    } else {
      exceptions.forEach((dbIds, model) => {
        if (dbIds.length > 0) {
          viewer.hide(dbIds, model);
        }
      });
    }
  }

  private static restoreSelection(
    viewer: Autodesk.Viewing.GuiViewer3D,
    selection: IInitialViewpoint["Selection"],
    modelMap: ModelDictionary
  ): void {
    try {
      viewer.clearSelection();
      if (typeof viewer.setAggregateSelection === 'function') {
        viewer.setAggregateSelection([]);
      }
    } catch (e) {
      console.warn("Error clearing selection:", e);
    }

    if (!selection || selection.length === 0) {
      return;
    }

    const grouped = this.groupComponentsByModel(selection, modelMap);
    const aggregate: any[] = [];

    grouped.forEach((dbIds, model) => {
      if (!Array.isArray(dbIds) || dbIds.length === 0) {
        return;
      }
      const filteredIds = dbIds.filter((id) => Number.isFinite(id));
      if (filteredIds.length === 0) {
        return;
      }
      if (model) {
        aggregate.push({ model, selection: filteredIds });
      }
    });

    if (aggregate.length > 0) {
      const validAggregate = aggregate.filter((entry) => {
        return (
          entry &&
          entry.model &&
          Array.isArray(entry.selection) &&
          entry.selection.length > 0 &&
          entry.selection.every((id: any) => Number.isFinite(id))
        );
      });
      
      if (validAggregate.length > 0) {
        try {
          setTimeout(() => {
            try {
              // Ensure proper format for setAggregateSelection - use 'ids' not 'selection'
              const formattedAggregate = validAggregate.map(({ model, selection }) => ({
                model,
                ids: Array.isArray(selection) ? selection : [selection]
              }));
              
              viewer.setAggregateSelection(formattedAggregate);
            } catch (error) {
              console.error("Error setting aggregate selection:", error);
              
              // Fallback to individual selections
              validAggregate.forEach(({ model, selection: dbIds }) => {
                try {
                  const ids = Array.isArray(dbIds) ? dbIds : [dbIds];
                  viewer.select(ids, model);
                } catch (fallbackError) {
                  console.error("Fallback selection also failed:", fallbackError);
                }
              });
            }
          }, 100);
        } catch (error) {
          console.error("Error in selection timeout:", error);
        }
      }
    }
  }

  private static restoreClippingPlanes(
    viewer: Autodesk.Viewing.GuiViewer3D,
    clippingPlanes: IInitialViewpoint["ClippingPlanes"]
  ): void {
    const THREE = (window as any).THREE || (Autodesk as any).THREE;
    if (!THREE) {
      return;
    }

    if (!clippingPlanes || clippingPlanes.length === 0) {
      (viewer.impl as any).setCutPlanes([]);
      return;
    }

    const planes = clippingPlanes.map((plane) => {
      const normal = new THREE.Vector3(plane.Direction.X, plane.Direction.Y, plane.Direction.Z);
      const location = new THREE.Vector3(plane.Location.X, plane.Location.Y, plane.Location.Z);
      const constant = -normal.dot(location);
      return new THREE.Vector4(normal.x, normal.y, normal.z, constant);
    });

    (viewer.impl as any).setCutPlanes(planes);
  }

  private static restoreBimFiles(
    viewer: Autodesk.Viewing.GuiViewer3D,
    bimFiles: IViewpointBimFile[] | undefined,
    modelMap: ModelDictionary
  ): void {
    if (!bimFiles || bimFiles.length === 0) {
      return;
    }

    bimFiles.forEach((file) => {
      const model =
        modelMap.get(file.Reference) ||
        modelMap.get(file.Reference?.replace(/^urn:/, "") || "");

      if (!model) {
        return;
      }

      const instanceTree = model.getData()?.instanceTree;
      if (!instanceTree) {
        return;
      }

      const rootId = instanceTree.getRootId();
      const visibilityManager = viewer.impl.visibilityManager as any;
      if (visibilityManager?.setNodeOff) {
        visibilityManager.setNodeOff(rootId, !file.IsVisible, model);
      }
    });
  }

  private static groupComponentsByModel(
    components: IViewpointComponent[] | undefined,
    modelMap: ModelDictionary
  ): Map<Autodesk.Viewing.Model, number[]> {
    const grouped = new Map<Autodesk.Viewing.Model, number[]>();

    if (!components) {
      return grouped;
    }

    components.forEach((component) => {
      const modelUrn = component.ComponentDetails?.ModelUrn;
      const dbIdRaw = component.ComponentDetails?.DbId || component.AuthoringToolId;
      const dbId = dbIdRaw ? Number(dbIdRaw) : NaN;

      if (!modelUrn || Number.isNaN(dbId)) {
        return;
      }

      const model =
        modelMap.get(modelUrn) || modelMap.get(modelUrn.replace(/^urn:/, ""));

      if (!model) {
        return;
      }

      const existing = grouped.get(model) || [];
      if (!existing.includes(dbId)) {
        existing.push(dbId);
      }
      grouped.set(model, existing);
    });

    return grouped;
  }
}
